import React, { useState, useRef, useEffect } from "react";
import { db, storage } from "./firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";

const VA_PP_DISTRICTS = [
  {n:1,label:"1 — Richmond"},{n:2,label:"2 — Norfolk"},{n:3,label:"3 — Portsmouth"},
  {n:4,label:"4 — Accomac"},{n:5,label:"5 — Gloucester"},{n:6,label:"6 — Suffolk"},
  {n:7,label:"7 — Petersburg"},{n:8,label:"8 — South Boston"},{n:9,label:"9 — Charlottesville"},
  {n:10,label:"10 — Arlington"},{n:11,label:"11 — Winchester"},{n:12,label:"12 — Staunton"},
  {n:13,label:"13 — Lynchburg"},{n:14,label:"14 — Danville"},{n:15,label:"15 — Roanoke"},
  {n:16,label:"16 — Wytheville"},{n:17,label:"17 — Abingdon"},{n:18,label:"18 — Norton"},
  {n:19,label:"19 — Newport News"},{n:20,label:"20 — Bedford"},{n:21,label:"21 — Fredericksburg"},
  {n:22,label:"22 — Martinsville"},{n:23,label:"23 — Virginia Beach"},{n:24,label:"24 — Farmville"},
  {n:25,label:"25 — Leesburg"},{n:26,label:"26 — Culpeper"},{n:27,label:"27 — Chesterfield"},
  {n:28,label:"28 — Radford"},{n:29,label:"29 — Fairfax"},{n:30,label:"30 — Hampton"},
  {n:31,label:"31 — Chesapeake"},{n:32,label:"32 — Henrico"},{n:33,label:"33 — Warsaw"},
  {n:34,label:"34 — Williamsburg"},{n:35,label:"35 — Manassas"},{n:36,label:"36 — Alexandria"},
  {n:37,label:"37 — Rocky Mount"},{n:38,label:"38 — Emporia"},{n:39,label:"39 — Harrisonburg"},
  {n:40,label:"40 — Fincastle"},{n:41,label:"41 — Ashland"},{n:42,label:"42 — Franklin"},
  {n:43,label:"43 — Tazewell"},
];

const VA_INSTITUTIONS = [
  "Bland Correctional Center","Cold Springs Correctional Unit","Green Rock Correctional Center",
  "Keen Mountain Correctional Center","Marion Correctional Treatment Center",
  "Patrick Henry Correctional Unit","Pocahontas State Correctional Center","Red Onion State Prison",
  "River North Correctional Center","Wallens Ridge State Prison","Wise Correctional Unit",
  "Baskerville Correctional Center","Beaumont Correctional Center","Buckingham Correctional Center",
  "Central Virginia Correctional Unit","Coffeewood Correctional Center","Dillwyn Correctional Center",
  "Halifax Correctional Unit","Lunenburg Correctional Center","Nottoway Correctional Center",
  "Rustburg Correctional Unit","State Farm Correctional Center","Caroline Correctional Unit",
  "Deerfield Correctional Center","Greensville Correctional Center","Haynesville Correctional Center",
  "Indian Creek Correctional Center","Lawrenceville Correctional Center","Augusta Correctional Center",
  "Sussex I State Prison","Sussex II State Prison","St. Brides Correctional Center","Other",
].sort((a,b)=>a==="Other"?1:b==="Other"?-1:a.localeCompare(b));

const RACE_OPTIONS = [
  "Black / African American","White / Caucasian","Hispanic / Latino",
  "Asian / Pacific Islander","Native American / Alaska Native","Multiracial","Other","Prefer not to say",
];

const OFFENSE_OPTIONS = [
  "Aggravated Malicious Wounding","Strangulation (Major Felony)","Robbery","Murder",
  "Assault (Various Categories)","Grand Larceny","Burglary","Arson","Embezzlement",
  "Possession with Intent to Distribute","Manufacturing Controlled Substances",
  "Multiple Marijuana Possession Offenses","Third-Offense DUI","Forgery","Money Laundering",
  "Reckless Driving","Rape (including Statutory)","Object Sexual Penetration",
  "Aggravated Sexual Battery","Forcible Sodomy","Taking Indecent Liberties with a Child",
  "Child Pornography Charges","Infected Sexual Battery","Abduction for Immoral Purpose",
  "Probation Violation","Parole Violation","Other",
];

const SUBSTANCE_OPTIONS = [
  "Alcohol","Marijuana / THC","Cocaine / Crack","Heroin","Fentanyl","Opioids (prescription)",
  "Methamphetamine","Benzodiazepines","PCP","MDMA / Ecstasy","Synthetic Cannabinoids (K2/Spice)",
  "Inhalants","Other",
];

// Required documents — removed Dorcus Referral Form and Signed Dorcus Rules
const CHECKLIST_ITEMS = [
  { label:"Consent to Release Information to Dorcus", required:true },
  { label:"VA DOC Institutional Disciplinary Action", required:true },
  { label:"Criminal History Narrative / VA DOC Sentencing Summary", required:true },
  { label:"Pre/Post Sentence Report / Trial Disposition", required:true },
  { label:"Medical Discharge Summary (incl. TB Test)", required:true },
  { label:"Offender Progress Report", required:true },
  { label:"Mental Health Records/Summary", required:false, condition:"mh" },
  { label:"Probation/Parole Conditions", required:false, condition:"pp" },
  { label:"Substance Abuse History/Treatment", required:false },
  { label:"Medication List", required:false },
  { label:"Medicaid/Medicare Transfer Confirmed", required:false },
];

const SECTIONS = [
  "Case Counselor",
  "Applicant Info",
  "Legal Background",
  "Health Background",
  "Mental Health",
  "Documents",
  "Additional Notes",
];

const TODAY = new Date().toISOString().split("T")[0];
const LS_KEY = "dorcus_referral_draft_v2";

const EMPTY_FORM = {
  referralSource:"institution", counselorName:"", counselorEmail:"", counselorPhone:"",
  institution:"", ppDistrict:"", referralDate:TODAY,
  firstName:"", lastName:"", doc:"", dob:"", race:"", sentencingDistrict:"",
  classification:"Non-Violent", gangAffiliated:"No",
  currentOffenses:[], priorOffenses:[],
  detainer:"No", icot:"No", priorDorcus:"",
  mhLevel:"MH-0", mentalHealthNotes:"",
  substanceAbuse:"No", substanceAbuseNotes:"", substanceList:[],
  physicalHealth:"No", physicalHealthNotes:"",
  checklist: Array(CHECKLIST_ITEMS.length).fill(false),
  additionalNotes:"",
};

// DS colors
const DS = {
  blue:"#1B5EBE", darkBlue:"#154A99", blueTint:"#E8F0FB", blueBorder:"#B5D4F4",
  pageBg:"#C8D8EC", border:"#D8DCE3", textPrimary:"#1A2B45", textSec:"#4A5568",
  success:{bg:"#E6F4EC",text:"#1A6B35",border:"#A3D4B4"},
  warning:{bg:"#FFF3CD",text:"#7A4F00",border:"#F5C200"},
  error:{bg:"#FDECEA",text:"#8B1A1A",border:"#F5A8A8"},
};

const S = {
  input:{ background:"white", border:`1px solid ${DS.border}`, borderRadius:8, padding:"10px 14px", fontSize:14, color:DS.textPrimary, width:"100%", outline:"none", boxSizing:"border-box", fontFamily:"inherit" },
  label:{ fontSize:13, fontWeight:600, color:DS.textPrimary, marginBottom:5, display:"block" },
  required:{ color:DS.error.text, marginLeft:3 },
  hint:{ fontSize:12, color:DS.textSec, marginTop:4 },
  error:{ fontSize:12, color:DS.error.text, marginTop:4 },
};

function Field({ label, required, hint, error, children }) {
  return (
    <div style={{marginBottom:16}}>
      <label style={S.label}>{label}{required&&<span style={S.required}>*</span>}</label>
      {children}
      {hint && <div style={S.hint}>{hint}</div>}
      {error && <div style={S.error}>{error}</div>}
    </div>
  );
}

function MultiSelect({ value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [otherText, setOtherText] = useState("");
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const toggle = (opt) => {
    if (opt === "Other") {
      const hasOther = value.some(v => !options.slice(0,-1).includes(v) || v === "Other");
      if (hasOther) { onChange(value.filter(v => options.slice(0,-1).includes(v) && v !== "Other")); setOtherText(""); }
      else onChange([...value, "Other"]);
      setOpen(false);
      return;
    }
    const next = value.includes(opt) ? value.filter(v=>v!==opt) : [...value.filter(v=>v!=="Other"), opt];
    onChange(next);
    setOpen(false);
  };
  const hasOther = value.some(v => !options.slice(0,-1).includes(v) || v === "Other");
  const displayText = value.length === 0 ? placeholder : `${value.length} selected`;
  return (
    <div ref={ref} style={{position:"relative"}}>
      <div onClick={()=>setOpen(o=>!o)} style={{...S.input, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between"}}>
        <span style={{color:value.length?DS.textPrimary:DS.textSec}}>{displayText}</span>
        <span style={{color:DS.textSec,flexShrink:0,marginLeft:8}}>{open?"▴":"▾"}</span>
      </div>
      {value.length > 0 && (
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:8}}>
          {value.map(v=>(
            <span key={v} style={{fontSize:12,padding:"3px 10px",borderRadius:12,background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,color:DS.blue,display:"flex",alignItems:"center",gap:6}}>
              {v.length>26?v.slice(0,24)+"…":v}
              <span onClick={()=>toggle(v==="Other"||!options.slice(0,-1).includes(v)?"Other":v)} style={{cursor:"pointer",fontWeight:700,fontSize:13}}>×</span>
            </span>
          ))}
        </div>
      )}
      {open && (
        <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,zIndex:100,background:"white",border:`1px solid ${DS.border}`,borderRadius:10,boxShadow:"0 10px 30px rgba(0,0,0,0.12)",maxHeight:240,overflowY:"auto"}}>
          {options.map(opt=>{
            const sel = opt==="Other" ? hasOther : value.includes(opt);
            return (
              <div key={opt} onClick={()=>toggle(opt)}
                style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",cursor:"pointer",background:sel?DS.blueTint:"white",borderBottom:`1px solid ${DS.border}`}}
                onMouseEnter={e=>e.currentTarget.style.background=sel?DS.blueTint:"#F0F4FA"}
                onMouseLeave={e=>e.currentTarget.style.background=sel?DS.blueTint:"white"}>
                <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${sel?DS.blue:DS.border}`,background:sel?DS.blue:"white",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {sel&&<span style={{color:"white",fontSize:10,fontWeight:700}}>✓</span>}
                </div>
                <span style={{fontSize:13,color:sel?DS.blue:DS.textPrimary}}>{opt}</span>
              </div>
            );
          })}
        </div>
      )}
      {hasOther && (
        <input style={{...S.input,marginTop:8}} placeholder="Describe further..." value={otherText}
          onChange={e=>{setOtherText(e.target.value);const base=value.filter(v=>options.slice(0,-1).includes(v)&&v!=="Other");onChange(e.target.value.trim()?[...base,e.target.value.trim()]:base);}}/>
      )}
    </div>
  );
}

function ChecklistUpload({ referralId, itemIndex, onFilesChange }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const updateFiles = (newFiles) => { setFiles(newFiles); onFilesChange(itemIndex, newFiles.length > 0); };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setProgress(0);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g,"_");
      const path = `documents/referrals/${referralId}/${itemIndex}/${Date.now()}_${safeName}`;
      const sRef = ref(storage, path);
      await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(sRef, file);
        task.on("state_changed",
          snap => setProgress(Math.round(snap.bytesTransferred/snap.totalBytes*100)),
          reject,
          async () => {
            const url = await getDownloadURL(task.snapshot.ref);
            updateFiles([...files, { name:file.name, url, path }]);
            resolve();
          }
        );
      });
    } catch(e) { alert("Upload failed: " + e.message); }
    finally { setUploading(false); setProgress(0); if(inputRef.current) inputRef.current.value=""; }
  };

  const removeFile = async (fileToRemove) => {
    try { await deleteObject(ref(storage, fileToRemove.path)); } catch(e) {}
    updateFiles(files.filter(f => f.path !== fileToRemove.path));
  };

  return (
    <div style={{marginTop:6}}>
      {files.map((f,i)=>(
        <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 10px",borderRadius:6,background:DS.success.bg,border:`1px solid ${DS.success.border}`,marginBottom:4}}>
          <span style={{fontSize:12,color:DS.success.text,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>✓ {f.name}</span>
          <a href={f.url} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:DS.blue,textDecoration:"none",flexShrink:0}}>View</a>
          <button type="button" onClick={()=>removeFile(f)} style={{fontSize:11,color:DS.error.text,background:"transparent",border:"none",cursor:"pointer",flexShrink:0,fontWeight:700,padding:"0 2px"}}>✕</button>
        </div>
      ))}
      {uploading && (
        <div style={{marginBottom:4}}>
          <div style={{height:3,background:DS.border,borderRadius:2,overflow:"hidden"}}>
            <div style={{width:`${progress}%`,height:"100%",background:DS.blue,transition:"width 0.2s"}}/>
          </div>
          <div style={{fontSize:11,color:DS.textSec,marginTop:2}}>Uploading {progress}%...</div>
        </div>
      )}
      <input ref={inputRef} type="file" style={{display:"none"}} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFile}/>
      <button type="button" onClick={()=>inputRef.current?.click()} disabled={uploading}
        style={{fontSize:11,padding:"4px 12px",borderRadius:6,cursor:uploading?"not-allowed":"pointer",background:"white",border:`1px solid ${DS.border}`,color:DS.textSec,opacity:uploading?0.5:1}}>
        {files.length>0?"+ Add Another File":"📎 Attach File"}
      </button>
    </div>
  );
}

// Generate Option B reference number: Initials-MMDDYY-Sequential
function generateRefNumber(firstName, lastName) {
  const initials = ((firstName||"X")[0]+(lastName||"X")[0]).toUpperCase();
  const now = new Date();
  const mm = String(now.getMonth()+1).padStart(2,"0");
  const dd = String(now.getDate()).padStart(2,"0");
  const yy = String(now.getFullYear()).slice(-2);
  const seq = String(Math.floor(Math.random()*900)+100);
  return `${initials}-${mm}${dd}${yy}-${seq}`;
}

export default function ReferralApp() {
  const [step, setStep] = useState(0);
  const [currentSection, setCurrentSection] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [referralId] = useState(() => "temp_" + Date.now() + "_" + Math.random().toString(36).slice(2,8));
  const [finalRefNumber, setFinalRefNumber] = useState(null);
  const [errors, setErrors] = useState({});
  const [showResumeBanner, setShowResumeBanner] = useState(false);
  const [rulesChecked, setRulesChecked] = useState(Array(10).fill(false));
  const [counselorConfirmed, setCounselorConfirmed] = useState(false);
  const sectionRefs = [useRef(null),useRef(null),useRef(null),useRef(null),useRef(null),useRef(null),useRef(null),useRef(null),useRef(null),useRef(null)];

  const [form, setForm] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      if (saved) { setShowResumeBanner(true); return JSON.parse(saved); }
    } catch(e) {}
    return { ...EMPTY_FORM };
  });

  useEffect(() => {
    if (step === 0) {
      try { localStorage.setItem(LS_KEY, JSON.stringify(form)); } catch(e) {}
    }
  }, [form]);

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const toggleChecklist = (i, checked) => {
    const c=[...form.checklist]; c[i]=checked !== undefined ? checked : !c[i]; set("checklist",c);
  };
  const handleFilesChange = (itemIndex, hasFiles) => {
    const c=[...form.checklist]; c[itemIndex]=hasFiles; set("checklist",c);
  };

  // Section completion for side nav
  const sectionComplete = [
    // 0 Case Counselor
    !!(form.counselorName.trim() && form.counselorEmail.trim() && (form.referralSource==="pp" ? form.ppDistrict : form.institution)),
    // 1 Applicant Info
    !!(form.firstName.trim() && form.lastName.trim() && form.doc.trim()),
    // 2 Legal Background
    form.currentOffenses.length > 0,
    // 3 Health Background
    form.hasHepatitis !== undefined || form.hasDiabetes !== undefined || form.hasSeizures !== undefined || form.healthNotes !== undefined,
    // 4 Mental Health
    form.mhHistory !== undefined || form.mhNotes !== undefined || form.mhLevel !== undefined,
    // 5 Documents
    form.checklist.some(Boolean),
    // 6 Additional Notes
    form.additionalNotes !== undefined || form.referralNotes !== undefined,
  ];

  const validate = () => {
    const e = {};
    if (!form.counselorName.trim())  e.counselorName  = "Required";
    if (!form.counselorEmail.trim()) e.counselorEmail = "Required";
    if (form.referralSource === "institution" && !form.institution) e.institution = "Required";
    if (form.referralSource === "pp" && !form.ppDistrict) e.ppDistrict = "Required";
    if (!form.firstName.trim())      e.firstName      = "Required";
    if (!form.lastName.trim())       e.lastName       = "Required";
    if (!form.doc.trim())            e.doc            = "Required";
    if (form.currentOffenses.length === 0) e.currentOffenses = "Select at least one offense";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) { window.scrollTo({top:0,behavior:"smooth"}); return; }
    setSubmitting(true);
    try {
      const refNo = generateRefNumber(form.firstName, form.lastName);
      const payload = {
        ...form,
        name: `${form.firstName} ${form.lastName}`.trim(),
        referredBy: form.counselorName,
        referralInstitution: form.referralSource === "institution" ? form.institution : "",
        ppDistrict: form.referralSource === "pp" ? form.ppDistrict : "",
        stage: "received",
        submittedDate: TODAY,
        submittedVia: "public_form",
        referenceNumber: refNo,
        tempId: referralId,
        createdAt: serverTimestamp(),
      };
      await addDoc(collection(db, "referrals"), payload);
      setFinalRefNumber(refNo);
      localStorage.removeItem(LS_KEY);
      setStep(2);
      window.scrollTo({top:0,behavior:"smooth"});
    } catch(err) {
      alert("Submission failed. Please try again. " + err.message);
    } finally { setSubmitting(false); }
  };

  // ── Submitted screen ──────────────────────────────────────────────────────
  if (step === 2) return (
    <div style={{minHeight:"100vh",background:DS.pageBg,fontFamily:"'Inter','Segoe UI',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet"/>
      <div style={{maxWidth:520,width:"100%",background:"white",borderRadius:10,padding:40,boxShadow:"0 4px 24px rgba(27,94,190,0.12)",textAlign:"center",border:`0.5px solid ${DS.border}`}}>
        <div style={{width:60,height:60,borderRadius:"50%",background:DS.success.bg,border:`2px solid ${DS.success.border}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",fontSize:28}}>✓</div>
        <h2 style={{fontSize:22,fontWeight:700,color:DS.textPrimary,marginBottom:12,fontFamily:"'DM Sans',sans-serif"}}>Referral Submitted</h2>
        <p style={{fontSize:14,color:DS.textSec,lineHeight:1.6,marginBottom:24}}>
          Thank you for submitting a referral for <strong style={{color:DS.textPrimary}}>{form.firstName} {form.lastName}</strong>.
          A member of our team will review the application and be in touch within <strong>3–5 business days</strong>.
        </p>
        <div style={{background:DS.blueTint,border:`1px solid ${DS.blueBorder}`,borderRadius:10,padding:"14px 20px",marginBottom:24,textAlign:"left"}}>
          <div style={{fontSize:11,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>Reference Number</div>
          <div style={{fontSize:18,fontWeight:700,color:DS.blue,fontFamily:"monospace",letterSpacing:"0.08em"}}>{finalRefNumber}</div>
          <div style={{fontSize:11,color:DS.textSec,marginTop:4}}>Keep this number for your records</div>
        </div>
        <p style={{fontSize:13,color:DS.textSec}}>
          Questions? Contact us at <a href="mailto:contact@dorcushomeva.org" style={{color:DS.blue}}>contact@dorcushomeva.org</a>
        </p>
      </div>
    </div>
  );

  // ── Rules screen ──────────────────────────────────────────────────────────
  const DORCUS_RULES = [
    { num:1, text:"I understand my participation in the 90 Day Dorcus Home Re-entry Program is voluntary. I understand that the Dorcus Home is contracted by the VADOC and as such is governed by their policies and procedures. I give the Dorcus Home my permission to obtain my identification, current and past criminal/institutional history, medical, behavioral, mental health, substance abuse, work and financial information from the VADOC, other agencies (including: VA Common help, VA Managed Care, DMV, Department of social services, social security administration and other criminal justice agencies, employers, community groups and public records as needed at the discretion of the Executive Director or designee." },
    { num:2, text:"I understand I must comply with all rules, guidelines, policies of the Dorcus Home re-entry Program. I further understand that any manipulation, violation or deviation from these policies may result in loss of privileges, sanctions, reports to my probation/parole officer or termination from the program. This includes rules regarding shared spaces, personal hygiene, weapons, drug paraphernalia, smoking areas, candles/open flames, chores, and respectful conduct toward all residents, guests, volunteers, contractors, and staff." },
    { num:3, text:"I understand that the Executive Director or designee will make appointments with various medical, behavioral health, counseling services, community resource agencies, employers and various Dorcus Home partners. I understand this is being done to facilitate my successful re-entry into society and I will keep these appointments or make arrangements to reschedule with the knowledge of designated staff. I understand that failure to adhere to appointments is my own personal decision and I will not hold Dorcus Home responsible for my missed appointments. I understand I must also meet with representatives of Virginia Cares and District 15 or local probation/parole office." },
    { num:4, text:"I understand that I may not use any drugs not prescribed by a physician for me. Any and all prescribed or over the counter medications will be immediately given to Dorcus Home Staff for storage and control. Medications will be issued to me by a staff member as prescribed. I WILL NOT use any other types of drugs both legal and illegal or consume any alcoholic drinks. I understand that Dorcus Home is a 100% Illegal Substance and Alcohol-free facility and program and conducts random drug and alcohol screens. My refusal to submit to a screen is grounds for dismissal and is reportable to Probation and Parole." },
    { num:5, text:"I understand that the Dorcus Home has Wi-Fi and Internet access available for use by residents for approved purposes only (job searches, employment applications, résumés, housing searches). Use of computers is limited to 1 hour unless authorized by staff and is a privilege, not a right. I also understand I must use all on-site laundry facilities at the designated times posted." },
    { num:6, text:"I understand that movement in and outside of the Dorcus Home Facility is monitored, logged and documented by staff. I may not enter or leave without checking in with front desk staff. I will not be allowed to leave or have visitors for the first 72 hours unless approved by the Executive Director. I have a 4-hour maximum leisure time. Curfew is 9:30PM each day except for work. Transitional visits and furloughs are a privilege that must be earned and approved by both my Probation and Parole Officer and the Executive Director." },
    { num:7, text:"I understand the Dorcus Home is primarily a back-to-work community residential program. I will make all reasonable efforts to find a job during my stay. I MUST TURN IN ALL FUNDS/MONEY I receive from any source to the Dorcus Home and will be given a receipt. Funds will be placed in a Resident Savings Account. I may only withdraw up to $80.00 per week for routine expenses. I must pay compensation to both the Department of Corrections and Dorcus Home at the rate of $5.00 each for every eight hours worked, not to exceed $25.00 per week each." },
    { num:8, text:"I understand I cannot operate or have an automobile while in the program. I am allowed at the approval of the Executive Director to own and operate a bicycle or scooter. No automobile can be stored or maintained on the premises. Per Department of Corrections Standards, I may only operate a motor vehicle for purposes of work and must have a state issued license." },
    { num:9, text:"I understand that to maintain order and accommodate limited living and storage space, Dorcus Home restricts the items and quantities I may store in my living space. I am not permitted to store items in cardboard boxes, garbage bags or any similar container. Allowed items include no more than: 7 sets of trousers and shirts, 10 sets of undergarments, 10 pairs of socks, 4 pairs of shoes/boots and 2 pairs of shower shoes, 2 light jackets and 2 heavy jackets or coats, and 4 hats or caps." },
    { num:10, text:"If I choose to abscond, am arrested, terminated or otherwise leave the program prior to successful conclusion, Dorcus Home will not accept responsibility for items left behind. An effort will be made by the Executive Director to gather my items, attempt to contact me based on the emergency contact I provided, and make arrangements for pickup within 1 week. After that period, these items will be considered abandoned property and disposed of at the discretion of the Executive Director." },
  ];

  if (step === 1) return (
    <div style={{minHeight:"100vh",background:DS.pageBg,fontFamily:"'Inter','Segoe UI',sans-serif"}}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet"/>
      <div style={{background:DS.blue,padding:"24px 24px 20px"}}>
        <div style={{maxWidth:700,margin:"0 auto"}}>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.7)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Dorcus Home · Step 2 of 2</div>
          <h1 style={{fontSize:22,fontWeight:700,color:"white",margin:0,marginBottom:6,fontFamily:"'DM Sans',sans-serif"}}>Acknowledgment of Rules & Guidelines</h1>
          <p style={{fontSize:13,color:"rgba(255,255,255,0.8)",margin:0}}>Review each item with the applicant and check the box to confirm acknowledgement before submitting.</p>
        </div>
      </div>
      <div style={{maxWidth:700,margin:"16px auto 40px",padding:"0 24px"}}>
        <div style={{background:"white",border:`1px solid ${DS.border}`,borderRadius:10,padding:16,marginBottom:16,fontSize:13,color:DS.textPrimary,lineHeight:1.6}}>
          The following items are the non-negotiable requirements of acceptance and participation in the Dorcus Home community residential and re-entry program. Any violation of any of these items may result in a formal report to your probation/parole officer and disciplinary actions including sanctions, loss of privileges, and program termination.
        </div>
        <div style={{background:"white",border:`1px solid ${DS.border}`,borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:12,color:DS.textPrimary,fontWeight:600,flexShrink:0}}>Progress</span>
          <div style={{flex:1,height:6,background:DS.border,borderRadius:3,overflow:"hidden"}}>
            <div style={{width:`${rulesChecked.filter(Boolean).length/10*100}%`,height:"100%",background:DS.success.text,borderRadius:3,transition:"width 0.2s"}}/>
          </div>
          <span style={{fontSize:12,color:DS.textSec,flexShrink:0}}>{rulesChecked.filter(Boolean).length} of 10 acknowledged</span>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
          {DORCUS_RULES.map((rule,i)=>(
            <div key={i} onClick={()=>{const c=[...rulesChecked];c[i]=!c[i];setRulesChecked(c);}}
              style={{background:rulesChecked[i]?DS.success.bg:"white",border:`1px solid ${rulesChecked[i]?DS.success.border:DS.border}`,borderRadius:10,padding:"14px 16px",cursor:"pointer",display:"flex",alignItems:"flex-start",gap:12,transition:"all 0.15s"}}>
              <div style={{width:20,height:20,borderRadius:5,border:`2px solid ${rulesChecked[i]?DS.success.text:DS.border}`,background:rulesChecked[i]?DS.success.text:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
                {rulesChecked[i]&&<span style={{color:"white",fontSize:11,fontWeight:700}}>✓</span>}
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:700,color:rulesChecked[i]?DS.success.text:DS.textPrimary,marginBottom:4}}>Item {rule.num}</div>
                <div style={{fontSize:12,color:rulesChecked[i]?DS.success.text:DS.textSec,lineHeight:1.6}}>{rule.text}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{background:"white",border:`2px solid ${DS.blue}`,borderRadius:12,padding:20,marginBottom:16}}>
          <div style={{fontSize:11,color:DS.blue,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:12}}>Counselor Confirmation</div>
          <div onClick={()=>setCounselorConfirmed(c=>!c)}
            style={{display:"flex",alignItems:"flex-start",gap:12,cursor:"pointer",marginBottom:16}}>
            <div style={{width:20,height:20,borderRadius:5,border:`2px solid ${counselorConfirmed?DS.blue:DS.border}`,background:counselorConfirmed?DS.blue:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
              {counselorConfirmed&&<span style={{color:"white",fontSize:11,fontWeight:700}}>✓</span>}
            </div>
            <div style={{fontSize:13,color:DS.textPrimary,lineHeight:1.5,fontWeight:500}}>
              I confirm I have reviewed these rules and guidelines with the applicant and both parties acknowledge and agree to the terms above.
            </div>
          </div>
          <button type="button" onClick={handleSubmit}
            disabled={!rulesChecked.every(Boolean)||!counselorConfirmed||submitting}
            style={{width:"100%",padding:"14px",fontSize:15,fontWeight:700,borderRadius:10,border:"none",color:"white",transition:"background 0.15s",
              cursor:(!rulesChecked.every(Boolean)||!counselorConfirmed||submitting)?"not-allowed":"pointer",
              background:(!rulesChecked.every(Boolean)||!counselorConfirmed||submitting)?DS.textSec:DS.blue}}>
            {submitting?"Submitting...":"Submit Application"}
          </button>
          {!rulesChecked.every(Boolean)&&<div style={{fontSize:11,color:DS.textSec,textAlign:"center",marginTop:8}}>All 10 items must be acknowledged before submitting</div>}
        </div>
        <div onClick={()=>{setStep(0);window.scrollTo({top:0,behavior:"smooth"});}}
          style={{fontSize:12,color:DS.textSec,textAlign:"center",textDecoration:"underline",cursor:"pointer",marginBottom:24}}>
          ← Back to application
        </div>
      </div>
    </div>
  );

  // ── Main form with side nav ───────────────────────────────────────────────
  const completedCount = sectionComplete.filter(Boolean).length;
  const progressPct = Math.round(completedCount / SECTIONS.length * 100);


  const scrollToSection = (i) => {
    setCurrentSection(i);
    sectionRefs[i]?.current?.scrollIntoView({behavior:"smooth", block:"start"});
  };

  return (
    <div style={{minHeight:"100vh",background:DS.pageBg,fontFamily:"'Inter','Segoe UI',sans-serif"}}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet"/>

      <div style={{background:DS.blue,padding:"20px 24px"}}>
        <div style={{maxWidth:900,margin:"0 auto"}}>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.7)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Dorcus Home · Step 1 of 2</div>
          <h1 style={{fontSize:24,fontWeight:700,color:"white",margin:0,marginBottom:4,fontFamily:"'DM Sans',sans-serif"}}>Resident Referral Application</h1>
          <p style={{fontSize:13,color:"rgba(255,255,255,0.8)",margin:0}}>Complete all required fields marked with <span style={{color:"#fcd34d"}}>*</span></p>
        </div>
      </div>

      <div style={{maxWidth:900,margin:"0 auto",padding:"16px 24px 40px",display:"grid",gridTemplateColumns:"180px 1fr",gap:20,alignItems:"start"}}>

        {/* Side nav */}
        <div style={{position:"sticky",top:16,background:"white",border:`0.5px solid ${DS.border}`,borderRadius:10,padding:14}}>
          <div style={{fontSize:10,color:DS.textSec,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10,fontFamily:"'Inter',sans-serif"}}>Application</div>
          {SECTIONS.map((s,i)=>(
            <div key={s} onClick={()=>scrollToSection(i)}
              style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",borderRadius:6,marginBottom:4,cursor:"pointer",
                background:currentSection===i?DS.blueTint:"transparent",
                transition:"background 0.15s"}}>
              <div style={{width:20,height:20,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
                background:sectionComplete[i]?DS.success.text:currentSection===i?DS.blue:DS.border,
                color:"white",fontSize:9,fontWeight:700}}>
                {sectionComplete[i]?"✓":i+1}
              </div>
              <span style={{fontSize:11,fontFamily:"'Inter',sans-serif",fontWeight:currentSection===i?500:400,
                color:sectionComplete[i]?DS.success.text:currentSection===i?DS.blue:DS.textSec,lineHeight:1.3}}>{s}</span>
            </div>
          ))}
          <div style={{height:1,background:DS.border,margin:"10px 0"}}/>
          <div style={{height:3,background:DS.border,borderRadius:2,overflow:"hidden",marginBottom:4}}>
            <div style={{width:`${progressPct}%`,height:"100%",background:progressPct===100?DS.success.text:DS.blue,borderRadius:2,transition:"width 0.3s"}}/>
          </div>
          <div style={{fontSize:10,color:progressPct===100?DS.success.text:DS.blue,fontFamily:"'Inter',sans-serif",textAlign:"center"}}>{progressPct}% complete</div>
        </div>

        {/* Form content */}
        <div>
          {showResumeBanner && (
            <div style={{background:DS.warning.bg,border:`1px solid ${DS.warning.border}`,borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
              <div style={{fontSize:13,color:DS.warning.text,fontWeight:500}}>You have an unfinished application.</div>
              <div style={{display:"flex",gap:8,flexShrink:0}}>
                <button type="button" onClick={()=>setShowResumeBanner(false)}
                  style={{fontSize:12,padding:"5px 12px",borderRadius:6,cursor:"pointer",background:DS.blue,border:"none",color:"white",fontWeight:600}}>Resume</button>
                <button type="button" onClick={()=>{localStorage.removeItem(LS_KEY);setForm({...EMPTY_FORM});setShowResumeBanner(false);}}
                  style={{fontSize:12,padding:"5px 12px",borderRadius:6,cursor:"pointer",background:"white",border:`1px solid ${DS.border}`,color:DS.textSec}}>Start Over</button>
              </div>
            </div>
          )}

          {/* Section 1: Case Counselor */}
          <div ref={sectionRefs[0]} style={{background:"white",border:`0.5px solid ${DS.border}`,borderRadius:10,padding:24,marginBottom:16}} onClick={()=>setCurrentSection(0)}>
            <div style={{fontSize:15,fontWeight:700,color:DS.textPrimary,marginBottom:16,paddingBottom:10,borderBottom:`1px solid ${DS.border}`,fontFamily:"'DM Sans',sans-serif"}}>
              1. Case Counselor Information
            </div>
            <Field label="Referring From" required>
              <div style={{display:"flex",gap:8}}>
                {[{v:"institution",l:"Institution / Facility"},{v:"pp",l:"Probation & Parole"}].map(({v,l})=>(
                  <button type="button" key={v} onClick={()=>set("referralSource",v)}
                    style={{flex:1,padding:"10px",borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600,
                      background:form.referralSource===v?DS.blue:"white",
                      border:`1.5px solid ${form.referralSource===v?DS.blue:DS.border}`,
                      color:form.referralSource===v?"white":DS.textPrimary}}>{l}</button>
                ))}
              </div>
            </Field>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <Field label="Counselor Full Name" required error={errors.counselorName}>
                <input style={{...S.input,borderColor:errors.counselorName?DS.error.text:DS.border}} value={form.counselorName} onChange={e=>set("counselorName",e.target.value)} placeholder="First Last"/>
              </Field>
              <Field label="Counselor Email" required error={errors.counselorEmail}>
                <input type="email" style={{...S.input,borderColor:errors.counselorEmail?DS.error.text:DS.border}} value={form.counselorEmail} onChange={e=>set("counselorEmail",e.target.value)} placeholder="email@institution.gov"/>
              </Field>
              <Field label="Counselor Phone" hint="Optional">
                <input type="tel" style={S.input} value={form.counselorPhone} onChange={e=>set("counselorPhone",e.target.value)} placeholder="(804) 555-0100"/>
              </Field>
              {form.referralSource === "institution" ? (
                <Field label="Institution / Facility" required error={errors.institution}>
                  <select style={{...S.input,borderColor:errors.institution?DS.error.text:DS.border}} value={form.institution} onChange={e=>set("institution",e.target.value)}>
                    <option value="">Select institution...</option>
                    {VA_INSTITUTIONS.map(i=><option key={i} value={i}>{i}</option>)}
                  </select>
                </Field>
              ) : (
                <Field label="P&P District" required error={errors.ppDistrict}>
                  <select style={{...S.input,borderColor:errors.ppDistrict?DS.error.text:DS.border}} value={form.ppDistrict} onChange={e=>set("ppDistrict",e.target.value)}>
                    <option value="">Select district...</option>
                    {VA_PP_DISTRICTS.map(d=><option key={d.n} value={String(d.n)}>{d.label}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Referral Date">
                <input type="date" style={S.input} value={form.referralDate} onChange={e=>set("referralDate",e.target.value)}/>
              </Field>
            </div>
          </div>

          {/* Section 2: Applicant Info */}
          <div ref={sectionRefs[1]} style={{background:"white",border:`0.5px solid ${DS.border}`,borderRadius:10,padding:24,marginBottom:16}} onClick={()=>setCurrentSection(1)}>
            <div style={{fontSize:15,fontWeight:700,color:DS.textPrimary,marginBottom:16,paddingBottom:10,borderBottom:`1px solid ${DS.border}`,fontFamily:"'DM Sans',sans-serif"}}>
              2. Applicant Information
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <Field label="First Name" required error={errors.firstName}>
                <input style={{...S.input,borderColor:errors.firstName?DS.error.text:DS.border}} value={form.firstName} onChange={e=>set("firstName",e.target.value)}/>
              </Field>
              <Field label="Last Name" required error={errors.lastName}>
                <input style={{...S.input,borderColor:errors.lastName?DS.error.text:DS.border}} value={form.lastName} onChange={e=>set("lastName",e.target.value)}/>
              </Field>
              <Field label="DOC Number" required error={errors.doc}>
                <input style={{...S.input,borderColor:errors.doc?DS.error.text:DS.border}} value={form.doc} onChange={e=>set("doc",e.target.value)} placeholder="e.g. 1234567"/>
              </Field>
              <Field label="Date of Birth">
                <input type="date" style={S.input} value={form.dob} onChange={e=>set("dob",e.target.value)}/>
              </Field>
              <Field label="Race">
                <select style={S.input} value={form.race} onChange={e=>set("race",e.target.value)}>
                  <option value="">Select race...</option>
                  {RACE_OPTIONS.map(r=><option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Sentencing District">
                <select style={S.input} value={form.sentencingDistrict} onChange={e=>set("sentencingDistrict",e.target.value)}>
                  <option value="">Select district...</option>
                  {VA_PP_DISTRICTS.map(d=><option key={d.n} value={String(d.n)}>{d.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Classification" required>
              <div style={{display:"flex",gap:8}}>
                {["Non-Violent","Violent","Sex Offender"].map(c=>(
                  <button type="button" key={c} onClick={()=>set("classification",c)}
                    style={{flex:1,padding:"9px 6px",borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600,
                      background:form.classification===c?DS.blue:"white",
                      border:`1.5px solid ${form.classification===c?DS.blue:DS.border}`,
                      color:form.classification===c?"white":DS.textPrimary}}>{c}</button>
                ))}
              </div>
            </Field>
            <Field label="Gang Affiliated?">
              <div style={{display:"flex",gap:8}}>
                {["Yes","No"].map(v=>(
                  <button type="button" key={v} onClick={()=>set("gangAffiliated",v)}
                    style={{flex:1,padding:"9px",borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600,
                      background:form.gangAffiliated===v?(v==="Yes"?DS.error.bg:DS.success.bg):"white",
                      border:`1.5px solid ${form.gangAffiliated===v?(v==="Yes"?DS.error.border:DS.success.border):DS.border}`,
                      color:form.gangAffiliated===v?(v==="Yes"?DS.error.text:DS.success.text):DS.textPrimary}}>{v}</button>
                ))}
              </div>
            </Field>
          </div>

          {/* Section 3: Legal Background */}
          <div ref={sectionRefs[2]} style={{background:"white",border:`0.5px solid ${DS.border}`,borderRadius:10,padding:24,marginBottom:16}} onClick={()=>setCurrentSection(2)}>
            <div style={{fontSize:15,fontWeight:700,color:DS.textPrimary,marginBottom:16,paddingBottom:10,borderBottom:`1px solid ${DS.border}`,fontFamily:"'DM Sans',sans-serif"}}>
              3. Legal Background
            </div>
            <Field label="Current Offense(s)" required error={errors.currentOffenses} hint="Select all that apply — includes probation/parole violations">
              <MultiSelect value={form.currentOffenses} onChange={v=>set("currentOffenses",v)} options={OFFENSE_OPTIONS} placeholder="Select offense(s)..."/>
            </Field>
            <Field label="Prior Offense(s)" hint="Select all that apply">
              <MultiSelect value={form.priorOffenses} onChange={v=>set("priorOffenses",v)} options={OFFENSE_OPTIONS} placeholder="Select offense(s)..."/>
            </Field>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
              {[{k:"detainer",l:"Active Detainer?"},{k:"icot",l:"ICOT in Progress?"}].map(({k,l})=>(
                <Field key={k} label={l}>
                  <div style={{display:"flex",gap:6}}>
                    {["Yes","No"].map(v=>(
                      <button type="button" key={v} onClick={()=>set(k,v)}
                        style={{flex:1,padding:"8px",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:600,
                          background:form[k]===v?(v==="Yes"?DS.error.bg:DS.success.bg):"white",
                          border:`1.5px solid ${form[k]===v?(v==="Yes"?DS.error.border:DS.success.border):DS.border}`,
                          color:form[k]===v?(v==="Yes"?DS.error.text:DS.success.text):DS.textPrimary}}>{v}</button>
                    ))}
                  </div>
                </Field>
              ))}
            </div>
            <Field label="Prior Dorcus Program History" hint="Leave blank if none">
              <input style={S.input} value={form.priorDorcus} onChange={e=>set("priorDorcus",e.target.value)} placeholder="e.g. Yes — 2021 — Completed successfully"/>
            </Field>
          </div>

          {/* Section 4: Health Background */}
          <div ref={sectionRefs[3]} style={{background:"white",border:`0.5px solid ${DS.border}`,borderRadius:10,padding:24,marginBottom:16}} onClick={()=>setCurrentSection(3)}>
            <div style={{fontSize:15,fontWeight:700,color:DS.textPrimary,marginBottom:16,paddingBottom:10,borderBottom:`1px solid ${DS.border}`,fontFamily:"'DM Sans',sans-serif"}}>
              4. Health Background
            </div>
            {/* Substance Abuse — selectable list */}
            <div style={{marginBottom:16}}>
              <Field label="Substance Abuse History">
                <div style={{display:"flex",gap:8,marginBottom:8}}>
                  {["Yes","No"].map(v=>(
                    <button type="button" key={v} onClick={()=>set("substanceAbuse",v)}
                      style={{flex:1,padding:"9px",borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600,
                        background:form.substanceAbuse===v?(v==="Yes"?DS.warning.bg:DS.success.bg):"white",
                        border:`1.5px solid ${form.substanceAbuse===v?(v==="Yes"?DS.warning.border:DS.success.border):DS.border}`,
                        color:form.substanceAbuse===v?(v==="Yes"?DS.warning.text:DS.success.text):DS.textPrimary}}>{v}</button>
                  ))}
                </div>
              </Field>
              {form.substanceAbuse === "Yes" && (
                <>
                  <Field label="Substances Used" hint="Select all that apply">
                    <MultiSelect value={form.substanceList||[]} onChange={v=>set("substanceList",v)} options={SUBSTANCE_OPTIONS} placeholder="Select substances..."/>
                  </Field>
                  <Field label="Substance Abuse Notes" hint="Treatment history, years of use, programs completed...">
                    <textarea rows={3} style={{...S.input,resize:"vertical"}} value={form.substanceAbuseNotes} onChange={e=>set("substanceAbuseNotes",e.target.value)} placeholder="Treatment history, years of use..."/>
                  </Field>
                </>
              )}
            </div>
            <div style={{marginBottom:16}}>
              <Field label="Physical Health Concerns">
                <div style={{display:"flex",gap:8,marginBottom:8}}>
                  {["Yes","No"].map(v=>(
                    <button type="button" key={v} onClick={()=>set("physicalHealth",v)}
                      style={{flex:1,padding:"9px",borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600,
                        background:form.physicalHealth===v?(v==="Yes"?DS.warning.bg:DS.success.bg):"white",
                        border:`1.5px solid ${form.physicalHealth===v?(v==="Yes"?DS.warning.border:DS.success.border):DS.border}`,
                        color:form.physicalHealth===v?(v==="Yes"?DS.warning.text:DS.success.text):DS.textPrimary}}>{v}</button>
                  ))}
                </div>
              </Field>
              {form.physicalHealth === "Yes" && (
                <textarea rows={3} style={{...S.input,resize:"vertical"}} value={form.physicalHealthNotes} onChange={e=>set("physicalHealthNotes",e.target.value)} placeholder="Chronic conditions, mobility issues, medications..."/>
              )}
            </div>
          </div>

          {/* Section 5: Mental Health */}
          <div ref={sectionRefs[4]} style={{background:"white",border:`0.5px solid ${DS.border}`,borderRadius:10,padding:24,marginBottom:16}} onClick={()=>setCurrentSection(4)}>
            <div style={{fontSize:15,fontWeight:700,color:DS.textPrimary,marginBottom:16,paddingBottom:10,borderBottom:`1px solid ${DS.border}`,fontFamily:"'DM Sans',sans-serif"}}>
              5. Mental Health Status
            </div>
            <Field label="MH Classification Level" hint="MH-0: No concerns · MH-1: Some concerns · MH-2: Active treatment required">
              <div style={{display:"flex",gap:8}}>
                {["MH-0","MH-1","MH-2"].map(v=>(
                  <button type="button" key={v} onClick={()=>set("mhLevel",v)}
                    style={{flex:1,padding:"10px 6px",borderRadius:8,cursor:"pointer",textAlign:"center",
                      background:form.mhLevel===v?DS.blueTint:"white",
                      border:`1.5px solid ${form.mhLevel===v?DS.blue:DS.border}`,
                      color:form.mhLevel===v?DS.blue:DS.textPrimary,fontWeight:700,fontSize:14}}>{v}</button>
                ))}
              </div>
            </Field>
            {form.mhLevel !== "MH-0" && (
              <Field label="Mental Health Notes">
                <textarea rows={3} style={{...S.input,resize:"vertical"}} value={form.mentalHealthNotes} onChange={e=>set("mentalHealthNotes",e.target.value)} placeholder="Relevant mental health history, diagnoses, current treatment..."/>
              </Field>
            )}
          </div>

          {/* Section 6: Documents */}
          <div ref={sectionRefs[5]} style={{background:"white",border:`0.5px solid ${DS.border}`,borderRadius:10,padding:24,marginBottom:16}} onClick={()=>setCurrentSection(5)}>
            <div style={{fontSize:15,fontWeight:700,color:DS.textPrimary,marginBottom:8,paddingBottom:10,borderBottom:`1px solid ${DS.border}`,fontFamily:"'DM Sans',sans-serif"}}>
              6. Supporting Documents
            </div>
            <p style={{fontSize:13,color:DS.textSec,marginBottom:16,marginTop:0}}>
              Upload files or check the box to indicate a document is forthcoming. Items marked <span style={{color:DS.error.text}}>*</span> are required.
            </p>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {CHECKLIST_ITEMS.map((item,i)=>{
                const isPP = form.referralSource === "pp";
                const needsMH = form.mhLevel === "MH-1" || form.mhLevel === "MH-2";
                const disabled = (item.condition==="pp" && !isPP) || (item.condition==="mh" && !needsMH);
                const disabledReason = item.condition==="pp" ? "Only required for P&P referrals" : "Only required for MH-1 or MH-2";
                return (
                  <div key={i} style={{borderRadius:10,border:`1px solid ${disabled?DS.border:form.checklist[i]?DS.success.border:DS.border}`,background:disabled?"#F0F4FA":form.checklist[i]?DS.success.bg:"white",overflow:"hidden",opacity:disabled?0.5:1}}>
                    <div onClick={()=>!disabled&&toggleChecklist(i)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",cursor:disabled?"default":"pointer"}}>
                      <div style={{width:18,height:18,borderRadius:5,border:`2px solid ${disabled?DS.border:form.checklist[i]?DS.success.text:DS.border}`,background:form.checklist[i]&&!disabled?DS.success.text:"white",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                        {form.checklist[i]&&!disabled&&<span style={{color:"white",fontSize:11,fontWeight:700}}>✓</span>}
                      </div>
                      <span style={{fontSize:13,color:disabled?DS.textSec:form.checklist[i]?DS.success.text:DS.textPrimary,fontWeight:form.checklist[i]&&!disabled?600:400,flex:1}}>
                        {item.label}{item.required&&<span style={{color:DS.error.text,marginLeft:3}}>*</span>}
                      </span>
                      {disabled&&<span style={{fontSize:10,color:DS.textSec,fontStyle:"italic"}}>{disabledReason}</span>}
                    </div>
                    {!disabled&&(
                      <div style={{padding:"0 14px 10px 42px"}}>
                        <ChecklistUpload referralId={referralId} itemIndex={i} onFilesChange={handleFilesChange}/>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{fontSize:12,color:DS.textSec,marginTop:12}}>
              {form.checklist.filter(Boolean).length} of {CHECKLIST_ITEMS.length} documents confirmed
            </div>
          </div>

          {/* Section 7: Additional Notes */}
          <div ref={sectionRefs[6]} style={{background:"white",border:`0.5px solid ${DS.border}`,borderRadius:10,padding:24,marginBottom:16}} onClick={()=>setCurrentSection(6)}>
            <div style={{fontSize:15,fontWeight:700,color:DS.textPrimary,marginBottom:16,paddingBottom:10,borderBottom:`1px solid ${DS.border}`,fontFamily:"'DM Sans',sans-serif"}}>
              7. Additional Notes
            </div>
            <Field label="Anything else we should know?" hint="Optional">
              <textarea rows={4} style={{...S.input,resize:"vertical"}} value={form.additionalNotes} onChange={e=>set("additionalNotes",e.target.value)} placeholder="Any additional information relevant to this referral..."/>
            </Field>
          </div>

          {Object.keys(errors).length > 0 && (
            <div style={{background:DS.error.bg,border:`1px solid ${DS.error.border}`,borderRadius:10,padding:"12px 16px",marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:600,color:DS.error.text,marginBottom:4}}>Please fix the following errors:</div>
              {Object.values(errors).map((e,i)=><div key={i} style={{fontSize:12,color:DS.error.text}}>• {e}</div>)}
            </div>
          )}

          <button type="button" onClick={()=>{if(!validate()){window.scrollTo({top:0,behavior:"smooth"});return;}setStep(1);window.scrollTo({top:0,behavior:"smooth"});}}
            style={{width:"100%",padding:"14px",fontSize:15,fontWeight:700,borderRadius:10,cursor:"pointer",background:DS.blue,border:"none",color:"white",fontFamily:"'Inter',sans-serif"}}>
            Continue to Rules Acknowledgement →
          </button>
          <p style={{fontSize:12,color:DS.textSec,textAlign:"center",marginTop:12}}>
            You will be asked to review and acknowledge the Dorcus Home Rules before submitting.
          </p>
        </div>
      </div>
    </div>
  );
}
